import React from "react";
import {
  generateOwnerDocument, generatePaDossier, listOwnerDocuments,
  listPaDocuments, newOperationId, ownerDocumentDownloadUrl,
} from "@/lib/vg2Documents";

/**
 * Bounded "Piano d'azione" / "Dossier" section — Phase 10.
 * variant="OWNER" uses owner endpoints; variant="PA" uses PA endpoints.
 */
export default function DocumentSection({ variant, caseId, paId }) {
  const [view, setView] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const v = variant === "PA"
        ? await listPaDocuments(paId, caseId)
        : await listOwnerDocuments(caseId);
      setView(v);
    } catch (e) { setError(e); }
  }, [variant, paId, caseId]);
  React.useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setError(null);
    try {
      const opId = newOperationId(variant === "PA" ? "doss" : "doc");
      if (variant === "PA") await generatePaDossier(paId, caseId, opId);
      else await generateOwnerDocument(caseId, opId);
      await load();
    } catch (e) { setError(e); }
    finally { setBusy(false); }
  };

  const title = variant === "PA" ? "Dossier" : "Piano d'azione";
  const tid = variant === "PA" ? "pa-document" : "owner-document";

  if (!view && !error) return (
    <section className="mb-6" data-testid={`${tid}-loading`}>
      <p className="text-sm text-vg2-muted">Caricamento documenti…</p>
    </section>
  );
  if (error) return (
    <section className="mb-6 border border-vg2-line rounded p-4"
             data-testid={`${tid}-error`}>
      <p className="text-sm text-vg2-red">Documenti: {error.message}</p>
    </section>
  );

  const eligible = view.eligibility_state === "ELIGIBLE";
  const current = view.current;
  return (
    <section className="mb-6" data-testid={`${tid}-root`}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-lg font-medium">{title}</h2>
        {current && (
          <span className={`text-xs px-2 py-1 rounded ${
            current.status === "CURRENT" ? "bg-vg2-subtle" : "bg-vg2-red text-white"
          }`}
                data-testid={`${tid}-status`}>
            {current.status_label}
          </span>
        )}
      </div>

      {!eligible && (
        <p className="text-xs text-vg2-red mb-3"
           data-testid={`${tid}-blocked`}>
          {view.eligibility_reason ||
           "Il documento non è ancora generabile per questa fase."}
        </p>
      )}

      {current && (
        <div className="border border-vg2-line rounded p-3 mb-3"
             data-testid={`${tid}-current`}>
          <p className="text-sm font-medium">{current.filename}</p>
          <p className="text-xs text-vg2-muted">
            Generato: {new Date(current.generated_at).toLocaleString("it-IT")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <a href={variant === "PA"
                    ? ownerDocumentDownloadUrl(caseId, current.document_id)
                    : ownerDocumentDownloadUrl(caseId, current.document_id)}
               target="_blank" rel="noreferrer"
               className="px-3 py-1 border border-vg2-red text-vg2-red rounded text-sm hover:bg-vg2-red hover:text-white"
               data-testid={`${tid}-download`}>
              {current.status === "CURRENT" ? "Scarica PDF" : "Scarica (versione storica)"}
            </a>
          </div>
        </div>
      )}

      <button onClick={generate} disabled={busy || !eligible}
              data-testid={`${tid}-generate`}
              className="px-4 py-2 bg-vg2-red text-white rounded text-sm disabled:opacity-40 hover:opacity-90">
        {busy ? "Generazione in corso…" :
          (current?.status === "STALE" || !current
            ? (current ? "Rigenera aggiornato" : "Genera documento")
            : "Rigenera")}
      </button>

      {view.history && view.history.length > 0 && (
        <details className="mt-3" data-testid={`${tid}-history`}>
          <summary className="text-xs text-vg2-muted cursor-pointer">
            Versioni storiche ({view.history.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {view.history.map((h) => (
              <li key={h.document_id}>
                <a href={ownerDocumentDownloadUrl(caseId, h.document_id)}
                   target="_blank" rel="noreferrer"
                   className="text-vg2-muted hover:text-vg2-ink underline">
                  {h.filename} · {h.status_label}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
